// =============================================================================
// MeuDriver — SlotPicker: seleção de data e horário disponível (H1)
// =============================================================================

import { useState, useMemo } from 'react'
import { format, addDays, isBefore, startOfDay } from 'date-fns'
import { ptBR } from 'date-fns/locale'
import { ChevronLeft, ChevronRight, Clock, CalendarDays, Loader2 } from 'lucide-react'
import { useAvailableSlots } from '@/hooks/useAvailability'
import { useBookingStore } from '@/store/bookingStore'
import { cn } from '@/lib/utils'
import { Skeleton } from '@/components/ui/skeleton'

// ─── Mini Calendar ────────────────────────────────────────────────────────────

const DAY_ABBR = ['D', 'S', 'T', 'Q', 'Q', 'S', 'S']
const LOOKAHEAD_DAYS = 30

interface MiniCalendarProps {
  selected: Date | null
  onSelect: (date: Date) => void
}

function MiniCalendar({ selected, onSelect }: MiniCalendarProps) {
  const today = startOfDay(new Date())
  const [offset, setOffset] = useState(0) // em semanas

  // Gera grid de 30 dias a partir de hoje, paginado em semanas de 7
  const allDays = useMemo(
    () =>
      Array.from({ length: LOOKAHEAD_DAYS }, (_, i) => addDays(today, i)),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // Semana visível (7 dias)
  const weekDays = allDays.slice(offset * 7, offset * 7 + 7)
  const maxOffset = Math.floor((LOOKAHEAD_DAYS - 1) / 7)

  return (
    <div className="space-y-2">
      {/* Header da semana */}
      <div className="flex items-center justify-between">
        <button
          type="button"
          disabled={offset === 0}
          onClick={() => setOffset((o) => o - 1)}
          className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Semana anterior"
        >
          <ChevronLeft className="h-4 w-4" />
        </button>

        <span className="text-xs font-semibold text-muted-foreground">
          {format(weekDays[0], 'MMMM yyyy', { locale: ptBR })}
        </span>

        <button
          type="button"
          disabled={offset >= maxOffset}
          onClick={() => setOffset((o) => o + 1)}
          className="flex h-7 w-7 items-center justify-center rounded-lg hover:bg-slate-100 dark:hover:bg-gray-800 disabled:opacity-30 disabled:cursor-not-allowed transition-colors"
          aria-label="Próxima semana"
        >
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Dias da semana header */}
      <div className="grid grid-cols-7 gap-1">
        {DAY_ABBR.map((d, i) => (
          <div key={i} className="text-center text-[10px] font-bold uppercase text-muted-foreground/60">
            {d}
          </div>
        ))}
      </div>

      {/* Botões de data */}
      <div className="grid grid-cols-7 gap-1">
        {weekDays.map((day) => {
          const isSelected =
            selected !== null &&
            format(selected, 'yyyy-MM-dd') === format(day, 'yyyy-MM-dd')
          const isPast = isBefore(day, today)

          return (
            <button
              key={day.toISOString()}
              type="button"
              disabled={isPast}
              onClick={() => onSelect(day)}
              data-testid={`slot-day-${format(day, 'yyyy-MM-dd')}`}
              className={cn(
                'flex flex-col items-center justify-center rounded-lg py-1.5 text-center transition-all text-sm font-medium',
                isPast && 'opacity-30 cursor-not-allowed',
                isSelected
                  ? 'bg-primary text-white shadow-sm'
                  : !isPast && 'hover:bg-primary/10 hover:text-primary',
              )}
            >
              <span className="text-xs leading-none">{format(day, 'dd')}</span>
            </button>
          )
        })}
      </div>
    </div>
  )
}

// ─── SlotPicker ───────────────────────────────────────────────────────────────

interface SlotPickerProps {
  instructorId: string
}

export function SlotPicker({ instructorId }: SlotPickerProps) {
  const store = useBookingStore()
  const selectedDate = store.selectedDate

  const dateStr = selectedDate ? format(selectedDate, 'yyyy-MM-dd') : undefined
  const { data: slots = [], isLoading } = useAvailableSlots(instructorId, dateStr)

  // Só horários disponíveis
  const available = slots.filter((s) => s.is_available)

  function handleSelectDate(date: Date) {
    store.setDate(date)
    // Limpa horário ao trocar data
    if (store.selectedTime) store.setTime('')
  }

  function handleSelectSlot(slotTime: string) {
    // slot_time: "HH:MM:SS" → queremos "HH:MM"
    store.setTime(slotTime.slice(0, 5))
  }

  return (
    <div className="space-y-5">
      {/* Mini calendário */}
      <div className="rounded-xl border p-4 dark:border-gray-700">
        <div className="flex items-center gap-2 mb-3">
          <CalendarDays className="h-4 w-4 text-primary" />
          <h4 className="text-sm font-semibold">Escolha a data</h4>
        </div>
        <MiniCalendar selected={selectedDate} onSelect={handleSelectDate} />
      </div>

      {/* Slots disponíveis */}
      {selectedDate && (
        <div className="rounded-xl border p-4 dark:border-gray-700">
          <div className="flex items-center gap-2 mb-3">
            <Clock className="h-4 w-4 text-primary" />
            <h4 className="text-sm font-semibold">
              Horários disponíveis —{' '}
              {format(selectedDate, "EEEE, d 'de' MMMM", { locale: ptBR })}
            </h4>
          </div>

          {isLoading ? (
            <div className="grid grid-cols-4 gap-2">
              {Array.from({ length: 8 }).map((_, i) => (
                <Skeleton key={i} className="h-10 rounded-lg" />
              ))}
            </div>
          ) : available.length === 0 ? (
            <p className="text-sm text-muted-foreground">
              Nenhum horário disponível nesta data. Escolha outro dia.
            </p>
          ) : (
            <div className="grid grid-cols-4 sm:grid-cols-5 gap-2">
              {available.map((s) => {
                const timeLabel = s.slot_time.slice(0, 5)
                const isSelected = store.selectedTime === timeLabel

                return (
                  <button
                    key={s.slot_time}
                    type="button"
                    onClick={() => handleSelectSlot(s.slot_time)}
                    data-testid={`slot-time-${timeLabel}`}
                    className={cn(
                      'py-2 px-3 rounded-lg text-sm font-medium border transition-colors',
                      isSelected
                        ? 'bg-primary text-white border-primary shadow-sm'
                        : 'bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 border-gray-200 dark:border-gray-700 hover:border-primary hover:text-primary',
                    )}
                  >
                    {timeLabel}
                  </button>
                )
              })}
            </div>
          )}

          {isLoading && (
            <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
              <Loader2 className="h-3 w-3 animate-spin" />
              Verificando disponibilidade...
            </div>
          )}
        </div>
      )}
    </div>
  )
}
